import React, { useState } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  CircularProgress,
  Alert,
  IconButton,
  InputAdornment,
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';

// モックモードかどうか
const isMockMode = import.meta.env.VITE_APP_MOCK_MODE === 'true';

interface ChangePasswordDialogProps {
  open: boolean;
  onClose: () => void;
}

/**
 * パスワード変更ダイアログ
 * Cognitoの updatePassword を使って現在のユーザーのパスワードを変更する
 */
const ChangePasswordDialog: React.FC<ChangePasswordDialogProps> = ({ open, onClose }) => {
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const reset = () => {
    setCurrentPassword('');
    setNewPassword('');
    setConfirmPassword('');
    setShowCurrent(false);
    setShowNew(false);
    setShowConfirm(false);
    setError('');
    setSuccess(false);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const validate = (): string => {
    if (!currentPassword) return '現在のパスワードを入力してください';
    if (!newPassword) return '新しいパスワードを入力してください';
    if (newPassword.length < 8) return '新しいパスワードは8文字以上で入力してください';
    if (newPassword !== confirmPassword) return '新しいパスワードが一致しません';
    if (currentPassword === newPassword) return '新しいパスワードは現在と異なるものを設定してください';
    return '';
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }

    setIsLoading(true);
    setError('');

    try {
      if (isMockMode) {
        // モックモードでは成功扱い
        await new Promise(resolve => setTimeout(resolve, 500));
      } else {
        const { updatePassword } = await import('aws-amplify/auth');
        await updatePassword({ oldPassword: currentPassword, newPassword });
      }
      setSuccess(true);
    } catch (err: unknown) {
      const errName = err instanceof Error ? err.name : '';
      if (errName === 'NotAuthorizedException') {
        setError('現在のパスワードが正しくありません');
      } else if (errName === 'InvalidPasswordException') {
        setError('新しいパスワードがポリシーを満たしていません');
      } else {
        setError('パスワードの変更に失敗しました');
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !isLoading) handleSubmit();
  };

  const showPasswordField = (
    label: string,
    value: string,
    onChange: (v: string) => void,
    show: boolean,
    onToggle: () => void,
  ) => (
    <TextField
      fullWidth
      label={label}
      type={show ? 'text' : 'password'}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={handleKeyDown}
      disabled={isLoading || success}
      sx={{ mt: 2 }}
      InputProps={{
        endAdornment: (
          <InputAdornment position="end">
            <IconButton onClick={onToggle} edge="end" size="small">
              {show ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
            </IconButton>
          </InputAdornment>
        ),
      }}
    />
  );

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <LockResetIcon />
        パスワード変更
      </DialogTitle>

      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mt: 1 }}>
            {error}
          </Alert>
        )}
        {success && (
          <Alert severity="success" sx={{ mt: 1 }}>
            パスワードを変更しました
          </Alert>
        )}

        {!success && (
          <>
            {showPasswordField(
              '現在のパスワード',
              currentPassword,
              setCurrentPassword,
              showCurrent,
              () => setShowCurrent(v => !v),
            )}
            {showPasswordField(
              '新しいパスワード',
              newPassword,
              setNewPassword,
              showNew,
              () => setShowNew(v => !v),
            )}
            {showPasswordField(
              '新しいパスワード（確認）',
              confirmPassword,
              setConfirmPassword,
              showConfirm,
              () => setShowConfirm(v => !v),
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose}>
          {success ? '閉じる' : 'キャンセル'}
        </Button>
        {!success && (
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isLoading}
            startIcon={isLoading ? <CircularProgress size={16} /> : <LockResetIcon />}
          >
            変更する
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default ChangePasswordDialog;
